import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Loader2, PawPrint } from 'lucide-react'

import { auth, ownerAuth, type PendingClaimProfile } from '../api/client'
import { ApiError } from '../api/types'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'

/**
 * Post-LINE-OAuth landing page for first-time merchants. Reads the
 * pending-claim cookie ticket (set by /auth/owner/line/callback) to
 * show "Welcome, {name}" + accepts the 6-digit code sales handed out.
 *
 * Cookie cross-origin caveat: api.vetos.pet sets the cookie on its own
 * domain, so owner.petpro.com.tw can't read it directly. The
 * /auth/owner/pending-claim endpoint must be called with `credentials:
 * 'include'`, which works ONLY when CORS Allow-Credentials is on. If the
 * profile fetch fails, we still render the form — the backend's
 * /auth/owner/claim call (also credentials:'include') is what actually
 * needs the cookie.
 */
export default function LoginClaim() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const [profile, setProfile] = useState<PendingClaimProfile | null>(null)
  const [profileLoading, setProfileLoading] = useState(true)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    ownerAuth.pendingClaim().then((p) => {
      if (!alive) return
      setProfile(p)
      setProfileLoading(false)
    })
    return () => {
      alive = false
    }
  }, [])

  async function submit() {
    if (busy) return
    const trimmed = code.trim()
    if (trimmed.length !== 6 || !/^\d{6}$/.test(trimmed)) {
      setErr(t('auth.claim.formatError'))
      return
    }
    setBusy(true)
    setErr(null)
    try {
      const tokens = await ownerAuth.claim(trimmed)
      auth.setPair(tokens.access_token, tokens.refresh_token)
      nav('/', { replace: true })
    } catch (e) {
      if (e instanceof ApiError) setErr(e.message)
      else setErr(t('auth.error.generic'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="container max-w-md py-16">
      <div className="mb-10 flex items-center gap-2 text-base font-semibold">
        <div className="grid size-9 place-items-center rounded-lg bg-primary text-primary-foreground">
          <PawPrint className="size-5" />
        </div>
        <span>{t('brand')}</span>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{t('auth.claim.title')}</CardTitle>
          <CardDescription>
            {profile?.name
              ? t('auth.claim.welcomeNamed', { name: profile.name })
              : t('auth.claim.welcomeAnon')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {profileLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              {t('loading')}
            </div>
          ) : (
            <>
              {profile?.picture && (
                <div className="flex items-center gap-3">
                  <img
                    src={profile.picture}
                    alt={profile.name ?? 'avatar'}
                    className="size-10 rounded-full border border-border bg-secondary/40"
                  />
                  <div className="text-sm text-muted-foreground">
                    {profile.email ?? t('auth.claim.noEmail')}
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="cc">{t('auth.claim.codeLabel')}</Label>
                <Input
                  id="cc"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="••••••"
                  className="text-center font-mono text-xl tracking-[0.5em]"
                  disabled={busy}
                />
                <p className="text-xs text-muted-foreground/80">
                  {t('auth.claim.codeHint')}
                </p>
              </div>

              {err && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {err}
                </div>
              )}

              <Button onClick={submit} disabled={busy || code.length !== 6} className="w-full">
                {busy ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <>
                    {t('auth.claim.submit')}
                    <ArrowRight className="ml-1 size-4" />
                  </>
                )}
              </Button>

              <p className="text-center text-xs text-muted-foreground/70">
                {t('auth.claim.noCodeHint')}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
